// ============================================================
// auth.js
// Authentication utilities — powered by Supabase Auth.
//
// WHAT CHANGED:
//   Previously used localStorage + SHA-256 hashing for a single user.
//   Now uses Supabase Auth for real multi-user accounts with:
//     - Email/password registration
//     - Email verification (user must confirm their email)
//     - Secure sessions that sync across devices
//     - Profile data stored in the cloud (username, CSV)
//
// HOW IT WORKS:
//   - signUp()     → creates account + sends verification email
//   - signIn()     → logs in with email + password
//   - signOut()    → logs out, clears session
//   - getSession() → checks if user is currently logged in
//   - getProfile() → fetches username from profiles table
//   - updateProfile() → updates username in profiles table
// ============================================================

import { supabase } from './supabase';
import { clearCachedCSV } from './csvCache';

const normalizeUsername = (value) => {
  const trimmed = String(value || '').trim();
  return trimmed || null;
};

const isUsableDisplayName = (value) => {
  const normalized = String(value || '').trim();
  const lowered = normalized.toLowerCase();
  return Boolean(normalized)
    && !normalized.includes('@')
    && lowered !== 'subscriber'
    && lowered !== 'trader'
    && lowered !== 'unknown';
};


// ── SIGN UP ─────────────────────────────────────────────────
// Creates a new account. Supabase sends a verification email
// automatically. The user must click the link before they can log in.
//
// username is stored in the profiles table (auto-created by a
// database trigger when the user signs up).
export const signUp = async (email, password, username, emailRedirectTo = null, metadata = {}) => {
  const normalizedUsername = normalizeUsername(username);
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        username: normalizedUsername || 'subscriber',
        ...metadata,
      },
      ...(emailRedirectTo ? { emailRedirectTo } : {}),
    },
  });

  return { data, error };
};


// ── SIGN IN ─────────────────────────────────────────────────
// Logs in with email + password. Returns a session token that
// Supabase stores in localStorage automatically.
// If the user hasn't verified their email yet, this will fail.
export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  return { data, error };
};


// ── SIGN OUT ────────────────────────────────────────────────
// Logs out the current user. Clears session and sensitive data
// from localStorage to prevent stale data exposure.
export const signOut = async () => {
  // Clear cached CSV data (contains financial info)
  clearCachedCSV();
  const { error } = await supabase.auth.signOut();
  return { error };
};


// ── GET SESSION ─────────────────────────────────────────────
// Returns the current session (if the user is logged in).
// Returns null if not logged in or session expired.
export const getSession = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session;
};


// ── GET CURRENT USER ────────────────────────────────────────
// Returns the currently authenticated user object.
// Contains: id, email, user_metadata, etc.
export const getUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};


// ── GET PROFILE ─────────────────────────────────────────────
// Fetches the user's profile (username) from the profiles table.
// The profiles row is auto-created by a database trigger on sign up.
export const getProfile = async () => {
  const user = await getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('profiles')
    .select('username, phone, status, zelle_name, zelle_email, zelle_phone, zelle_updated_at')
    .eq('id', user.id)
    .limit(1)
    .maybeSingle();

  if (error) return null;

  const metadataName = user.user_metadata?.username || user.user_metadata?.full_name || user.user_metadata?.name;
  return {
    ...data,
    username: isUsableDisplayName(data?.username)
      ? data.username
      : (isUsableDisplayName(metadataName) ? metadataName : data?.username),
  };
};


// ── UPDATE PROFILE ──────────────────────────────────────────
// Updates the user's username in the profiles table.
export const updateProfile = async (username, updates = {}) => {
  const user = await getUser();
  if (!user) return { error: { message: 'Not logged in' } };

  const normalizedUsername = String(username || '').trim();
  const patch = {
    username: normalizedUsername,
    ...(Object.prototype.hasOwnProperty.call(updates, 'phone')
      ? { phone: String(updates.phone || '').trim() || null }
      : {}),
  };

  const { data, error } = await supabase
    .from('profiles')
    .update(patch)
    .eq('id', user.id)
    .select('username, phone')
    .maybeSingle();

  if (error) return { error };

  if (!data?.username) {
    const { data: upserted, error: upsertError } = await supabase
      .from('profiles')
      .upsert({ id: user.id, ...patch }, { onConflict: 'id' })
      .select('username, phone')
      .single();

    if (upsertError) return { error: upsertError };
    return { error: null, data: upserted };
  }

  return { error: null, data };
};


// ── UPDATE PASSWORD ─────────────────────────────────────────
// Changes the user's password. Requires the user to be logged in.
// Supabase handles the hashing — we just send the new password.
export const updatePassword = async (newPassword) => {
  const { error } = await supabase.auth.updateUser({
    password: newPassword,
  });
  return { error };
};

export const updateEmail = async (email) => {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  if (!normalizedEmail) {
    return { error: { message: 'Email is required.' } };
  }

  const { data, error } = await supabase.auth.updateUser({
    email: normalizedEmail,
  });

  return { data, error };
};

export const sendPasswordResetEmail = async (email) => {
  const normalizedEmail = String(email || '').trim().toLowerCase();
  if (!normalizedEmail) {
    return { error: { message: 'Email is required.' } };
  }

  const { data, error } = await supabase.auth.resetPasswordForEmail(normalizedEmail);
  return { data, error };
};


// ── SAVE CSV TO CLOUD ───────────────────────────────────────
// Stores the raw CSV text in the csv_data table, linked to the
// current user. Uses upsert so uploading a new CSV replaces the old one.
export const saveCSVToCloud = async (csvText, fileName) => {
  const user = await getUser();
  if (!user) return { error: { message: 'Not logged in' } };

  const { error } = await supabase
    .from('csv_data')
    .upsert(
      {
        user_id:     user.id,
        csv_text:    csvText,
        file_name:   fileName || 'account.csv',
        uploaded_at: new Date().toISOString(),
      },
      { onConflict: 'user_id' }  // one CSV per user — replace on re-upload
    );

  return { error };
};


// ── LOAD CSV FROM CLOUD ─────────────────────────────────────
// Fetches the user's stored CSV text from the database.
// Returns null if no CSV has been uploaded yet.
export const loadCSVFromCloud = async () => {
  const user = await getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('csv_data')
    .select('csv_text, file_name, uploaded_at')
    .eq('user_id', user.id)
    .single();

  if (error || !data) return null;
  return data;
};


// ── DELETE CSV FROM CLOUD ───────────────────────────────────
// Removes the user's stored CSV data from the database.
export const deleteCSVFromCloud = async () => {
  const user = await getUser();
  if (!user) return { error: { message: 'Not logged in' } };

  const { error } = await supabase
    .from('csv_data')
    .delete()
    .eq('user_id', user.id);

  return { error };
};


// ── AUTH STATE LISTENER ─────────────────────────────────────
// Subscribes to auth state changes (login, logout, token refresh).
// Returns an unsubscribe function — call it in useEffect cleanup.
//
// Usage:
//   useEffect(() => {
//     const unsub = onAuthChange((event, session) => { ... });
//     return unsub;
//   }, []);
export const onAuthChange = (callback) => {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(callback);
  return () => subscription.unsubscribe();
};
